import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@devinforius/super-compos-17';
import { IModalHeader } from '@devinforius/super-compos-17/lib/i-modal/models/i-modal-header.model';
import { IModalParams } from '@devinforius/super-compos-17/lib/i-modal/models/i-modal-parameters.model';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from 'src/app/core/services/api.service';
import { iAdminService } from 'src/app/core/services/iAdmin.service';
import { IDelibeMeService } from 'src/app/core/services/idelibe/me.service';
import { Point } from 'src/app/shared/models/point';
import { ActionType, CategoryActions } from 'src/app/shared/models/point/category-actions.model';
import { MeRightPoint } from 'src/app/shared/models/point/me-right-point.model';
import { PointSeance, TypeSeanceRights } from 'src/app/shared/models/point/point-seance';
import { TenantInfo } from 'src/app/shared/models/tenant-info';

@Component({
  selector: 'app-actions-point',
  templateUrl: './actions-point.component.html',
  styleUrls: ['./actions-point.component.scss'],
})
export class ActionsPointComponent implements OnInit {
  // Inuput
  @Input() public point: Point;
  @Input() public canOpenPoint: boolean = true;

  // Output
  @Output() public action: EventEmitter<any> = new EventEmitter();

  // Variables
  categoryActions: CategoryActions[] = [];
  meRightPoint: MeRightPoint;
  rightsPointsSeances: TypeSeanceRights;
  tenantICourrier: boolean = false;
  canCreateObjectModel: boolean = false;
  modalParams: IModalParams;
  modalHeader: IModalHeader;

  constructor(
    public api: ApiService,
    public IDelibeMeService: IDelibeMeService,
    public apiAdmin: iAdminService,
    public toastr: ToastrService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.setModalConfiguration();
    forkJoin({
      rights: this.getRights(),
      tenant: this.getTenant(),
      rightPoint: this.getRightsPointsSeances(),
      rightAdmin: this.getRightAdmin(),
    }).subscribe({
      next: () => {
        this.setAction();
      },
      error: (error) => {
        console.error('Error in one of the observables:', error);
      },
    });
  }

  setModalConfiguration(): void {
    this.modalHeader = {
      title: this.translate.translate('action'),
      iconTitle: 'bi bi-lightning-charge-fill',
      showNotification: false,
      showParameters: false,
      closable: true,
    };
    this.modalParams = {
      isDraggable: true,
      isResizable: true,
      isModal: true,
      style: { width: '30vw', minWidth: '30vw', maxWidth: '30vw' },
    };
  }

  getRights(): Observable<boolean> {
    return this.IDelibeMeService.getRightsPoint(this.point.id).pipe(
      catchError((error) => {
        console.error('Error fetching right info:', error);
        this.toastr.error(this.translate.translate('error.retrievingRightPoint'));
        return of(null);
      }),
      map((res: MeRightPoint) => {
        this.meRightPoint = res;
        return true;
      })
    );
  }

  getRightsPointsSeances() {
    return this.IDelibeMeService.getRightsPointsSeances().pipe(
      catchError((error) => {
        console.error('Error fetching right info:', error);
        this.toastr.error(this.translate.translate('error.retrievingRightPoint'));
        return of(null);
      }),
      map((res: PointSeance) => {
        this.rightsPointsSeances = res.typeSeanceRights.find((right) => right.typeSeanceId === this.point.typeSeanceId);
        return true;
      })
    );
  }

  getTenant(): Observable<boolean> {
    return this.apiAdmin.getTenant().pipe(
      catchError((error) => {
        console.error('Error fetching tenant info:', error);
        this.toastr.error(this.translate.translate('error.retrievingTenantInfo'));
        return of(null);
      }),
      map((res: TenantInfo) => {
        res.applications.some((app) => app.id === 5) ? (this.tenantICourrier = true) : (this.tenantICourrier = false);
        return true;
      })
    );
  }

  getRightAdmin(): Observable<boolean> {
    return forkJoin({
      rightManagement: this.IDelibeMeService.getRightsManagement().pipe(
        catchError((error) => {
          console.error('Error fetching rights management:', error);
          this.toastr.error(this.translate.translate('error.retrievingDelibeRightsInfo'));
          return of(null);
        })
      ),
      userRights: this.IDelibeMeService.getUserRights().pipe(
        catchError((error) => {
          console.error('Error fetching admin rights management:', error);
          this.toastr.error(this.translate.translate('error.retrievingAdminRightsInfo'));
          return of(null);
        })
      ),
    }).pipe(
      map(({ rightManagement, userRights }) => {
        const modelObject = rightManagement?.menu?.modeleObjets || false;

        this.canCreateObjectModel = modelObject;
        return true;
      })
    );
  }

  setAction(): void {
    this.categoryActions = [
      {
        label: this.translate.translate('gestion'),
        actions: [
          {
            label: this.translate.translate('btn.openPoint'),
            img: './assets/img/open.svg',
            action: () => this.handleClick(ActionType.OpenPoint, this.point),
            condition: this.canOpenPoint,
          },
          {
            label: this.translate.translate('btn.modify'),
            img: './assets/img/edit.svg',
            action: () => this.handleClick(ActionType.UpdatePoint, this.point),
            condition: this.meRightPoint.edit,
          },
          {
            label: this.translate.translate('btn.duplicate'),
            img: './assets/img/duplicate.svg',
            action: () => this.handleClick(ActionType.DuplicatePoint, this.point),
            condition: this.meRightPoint.duplicate,
          },
          {
            label: this.translate.translate('report'),
            img: './assets/img/postpone.svg',
            action: () => this.handleClick(ActionType.PostponePoint, this.point),
            condition: this.meRightPoint.report,
          },
          {
            label: this.translate.translate('createPointModel'),
            icon: 'bi bi-plus-circle-fill',
            action: () => this.handleClick(ActionType.OpenCreationModelPointBasedOnExistingPoint, true),
            condition: this.canCreateObjectModel,
          },
          {
            label: this.translate.translate('createOutgoingMail'),
            img: './assets/img/iCourrier.png',
            action: () => this.handleClick(ActionType.CreateOutgoingMail, this.point),
            condition: this.tenantICourrier,
            isIconClass: true,
          },
          {
            label: this.translate.translate('btn.removeFromMeeting'),
            img: './assets/img/delete.svg',
            action: () => this.handleClick(ActionType.DeletePoint, this.point),
            condition: this.meRightPoint.removeSeance,
          },
        ],
      },
      {
        label: this.translate.translate('title.pointInMeeting'),
        actions: [
          {
            label: 'Déplacer le point',
            icon: 'bi bi-arrow-down-up',
            action: () => this.handleClick(ActionType.MovePoint, this.point),
            condition: this.meRightPoint.isGestionnaire,
          },
          {
            label: this.translate.translate('addEventBefore'),
            img: './assets/img/event.svg',
            action: () => this.handleClick(ActionType.AddEventBefore, true),
            condition: this.meRightPoint.isGestionnaire,
          },
        ],
      },
      {
        label: this.translate.translate('title.pointMarking'),
        actions: [
          {
            label: this.translate.translate('global.closed'),
            img: './assets/img/closed.svg',
            action: () => this.handleClick(ActionType.FinishUnfinishPoint, this.point),
            condition: this.meRightPoint.cloture,
          },
          {
            label: this.translate.translate('btn.opened'),
            img: './assets/img/unclosed.svg',
            action: () => this.handleClick(ActionType.FinishUnfinishPoint, this.point),
            condition: this.meRightPoint.decloture,
          },
          {
            label: this.translate.translate('btn.unlocked'),
            img: './assets/img/unlocked.svg',
            action: () => this.handleClick(ActionType.LockUnlock, this.point),
            condition: this.meRightPoint.deverrouille,
          },
          {
            label: this.translate.translate('btn.locked'),
            img: './assets/img/locked.svg',
            action: () => this.handleClick(ActionType.LockUnlock, this.point),
            condition: this.meRightPoint.verrouille,
          },
          {
            label: this.translate.translate('btn.toModify'),
            img: './assets/img/tofix.svg',
            action: () => this.handleClick(ActionType.MarkACorriger, this.point),
            condition: this.meRightPoint.corrections,
          },
          {
            label: this.translate.translate('btn.validateTheCorrection'),
            img: './assets/img/fixed.svg',
            action: () => this.handleClick(ActionType.ValidateCorrection, this.point),
            condition: this.meRightPoint.validerCorrections,
          },
          {
            label: this.translate.translate('btn.unanimouslyApproved'),
            img: './assets/img/approved.svg',
            action: () => this.handleClick(ActionType.ApproveUnanimously, this.point),
            condition: this.meRightPoint.isGestionnaire,
          },
        ],
      },
    ];

    // Filtrer les catégories d'actions pour ne pas afficher celles dont toutes les conditions sont fausses
    this.categoryActions = this.categoryActions.filter((category) =>
      category.actions.some((action) => action.condition)
    );
  }

  handleClick(action: any, dataToSend: any): void {
    this.action.emit({ action, dataToSend });
  }

  closeDialog(): void {
    this.handleClick(ActionType.CloseModal, false);
  }
}
